import React, { Component } from 'react';
import './App.css';
import * as authentication from './authentication';
import * as progress from './progress';
import { getMeditation, getTimeline } from './meditations';
import Pelotita from './Pelotita';
import AccountForm from './AccountForm';
import { count } from './measurement';

class App extends Component {
  state = {
    skipIntro: false, playing: false, completed: false, scriptIndex: 0, script: 'ONE_MINUTE',
    user: null, stats: { sessions: 0, timeMeditated: 0 }, accountOpen: false, accountMode: 'login',
    installAvailable: false, message: '', soundMessage: '', syncMessage: '',
  };

  timeout = null;
  startedAt = null;
  timeline = [];
  wakeLock = null;
  audio = new Audio('/sounds/bell.mp3');

  componentDidMount() {
    try {
      progress.preserveLegacy();
      this.setState({ stats: progress.getStats() });
    } catch {
      this.setState({ message: 'This browser cannot save progress. Check its storage settings before starting.' });
    }
    this.unsubscribeStats = progress.onStatsChanged(stats => this.setState({ stats }));
    this.unsubscribeAuth = authentication.onUserDataChanged(user => {
      progress.setUser(user);
      this.setState({ user, syncMessage: '' }, this.sync);
    });
    authentication.init().then(() => count('visit')).catch(() => this.setState({ syncMessage: 'Offline: you can still meditate. Progress stays on this device.' }));
    window.addEventListener('beforeinstallprompt', this.onInstallPrompt);
    window.addEventListener('online', this.reconnect);
    window.addEventListener('storage', this.onStorage);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  componentDidUpdate(previousProps, previousState) {
    if (previousState.playing !== this.state.playing || previousState.completed !== this.state.completed) {
      const target = this.state.completed ? '#completion-title' : '#circle-text';
      document.querySelector(target)?.focus();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
    this.releaseWakeLock();
    this.audio.pause();
    this.unsubscribeStats?.();
    this.unsubscribeAuth?.();
    window.removeEventListener('beforeinstallprompt', this.onInstallPrompt);
    window.removeEventListener('online', this.reconnect);
    window.removeEventListener('storage', this.onStorage);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  sync = async () => {
    try {
      await progress.sync();
      this.setState({ syncMessage: this.state.user ? 'Progress saved to your account.' : '' });
    } catch (error) {
      this.setState({ syncMessage: error.status === 401 || error.status === 409
        ? 'Please log in again to save your progress. It is still on this device.'
        : 'Progress is on this device. We will retry when you reconnect.' });
    }
  };

  reconnect = () => authentication.init().catch(() => this.setState({ syncMessage: 'Unable to connect. Progress stays on this device.' }));
  onStorage = event => { if (event.key?.startsWith('voidhead.')) progress.refresh(); };
  onInstallPrompt = event => {
    event.preventDefault();
    this.installPrompt = event;
    this.setState({ installAvailable: true });
  };
  install = async () => {
    await this.installPrompt?.prompt();
    this.installPrompt = null;
    this.setState({ installAvailable: false });
  };

  requestWakeLock = async () => {
    if (!navigator.wakeLock || document.visibilityState !== 'visible') return;
    try {
      const lock = await navigator.wakeLock.request('screen');
      if (this.startedAt === null) await lock.release();
      else this.wakeLock = lock;
    } catch { /* The visible session still works when the browser refuses a wake lock. */ }
  };

  releaseWakeLock = () => {
    this.wakeLock?.release().catch(() => {});
    this.wakeLock = null;
  };

  onVisibility = () => {
    if (document.visibilityState !== 'visible' || this.startedAt === null) return;
    this.tick();
    this.requestWakeLock();
  };

  ring = () => {
    this.audio.currentTime = 0;
    this.audio.play().catch(error => {
      if (error.name !== 'AbortError') this.setState({ soundMessage: 'Sound could not play. Check your browser sound settings.' });
    });
  };

  play = () => {
    clearTimeout(this.timeout);
    this.timeline = getTimeline(this.state.script, this.state.skipIntro);
    this.startedAt = Date.now();
    this.lastIndex = -1;
    this.setState({ playing: true, completed: false, accountOpen: false, scriptIndex: 0, soundMessage: '', message: '' }, this.tick);
    this.ring();
    count('start');
    this.requestWakeLock();
  };

  tick = () => {
    clearTimeout(this.timeout);
    if (this.startedAt === null) return;
    const elapsed = Date.now() - this.startedAt;
    const index = this.timeline.findIndex(line => line.end > elapsed);
    if (index === -1) {
      this.startedAt = null;
      this.releaseWakeLock();
      this.setState({ playing: false, completed: true, scriptIndex: 0 });
      this.ring();
      count('complete');
      try {
        progress.complete(getMeditation(this.state.script).duration);
        this.sync();
      } catch {
        this.setState({ message: 'Meditation completed, but your browser could not save it. Please check its storage settings.' });
      }
      return;
    }
    if (index !== this.lastIndex) {
      this.lastIndex = index;
      this.setState({ scriptIndex: index });
      if (this.timeline[index].sound) this.ring();
    }
    this.timeout = setTimeout(this.tick, Math.max(1, this.timeline[index].end - elapsed));
  };

  stop = () => {
    clearTimeout(this.timeout);
    this.startedAt = null;
    this.audio.pause();
    this.releaseWakeLock();
    this.setState({ playing: false, completed: false, scriptIndex: 0 });
  };

  setScript = script => {
    this.stop();
    this.setState({ script });
  };

  logout = async () => {
    try {
      await authentication.logout();
    } catch {
      this.setState({ message: 'Could not log out. Reconnect and try again.' });
    }
  };

  render() {
    const { playing, completed, script, skipIntro, user, stats } = this.state;
    const line = playing ? this.timeline[this.state.scriptIndex] : null;
    const timeline = getTimeline(script, skipIntro);
    const total = Math.ceil(timeline[timeline.length - 1].end / 1000);
    return <div className={`App${playing ? ' App--playing' : ''}`}>
      <header>
        <h1>voidhead</h1>
        {!playing && !completed && <h2>A little space to breathe.</h2>}
      </header>
      <main>
        {!playing && !completed && <p className="intro">Short guided meditations. No account needed.</p>}
        {!completed && <Pelotita animation={line?.animation} playing={playing} onPlay={this.play} onStop={this.stop} />}
        {!playing && !completed && <div id="time-selector">
          <div className="duration-options" aria-label="Meditation length">{[['ONE_MINUTE', '1 min'], ['FIVE_MINUTES', '5 min'], ['TEN_MINUTES', '10 min']].map(([id, title]) =>
            <button className="btn duration-option" key={id}
              aria-pressed={script === id} onClick={() => this.setScript(id)}>{title}</button>)}</div>
          <label><input type="checkbox" checked={skipIntro} onChange={event => this.setState({ skipIntro: event.target.checked })} /> Skip intro</label>
          <p className="small">{getMeditation(script).duration / 60} {getMeditation(script).duration === 60 ? 'minute' : 'minutes'} of quiet practice; about {Math.floor(total / 60)}:{String(total % 60).padStart(2, '0')} including guidance.</p>
        </div>}
        {playing && <div id="script-now" key={this.state.scriptIndex} className="fade-in" aria-live="polite">{line?.text.split('\n').map((phrase, index) => <span key={index}>{phrase}</span>)}</div>}
        {playing && <p className="small playback-note">Keep this page open and your screen unlocked for reliable bells. Sound may pause in the background.</p>}
        {this.state.soundMessage && <p role="status">{this.state.soundMessage}</p>}
        {this.state.message && <p role="alert">{this.state.message}</p>}
        {completed && <section className="completion" aria-labelledby="completion-title">
          <h2 id="completion-title" tabIndex={-1}>Meditation completed!</h2>
          <p>Take a moment before moving on.</p>
          <button className="btn btn-secondary" onClick={this.stop}>Back to meditations</button>
          <div className="completion-support">
          <p>If this little pause was useful, you can <a href="https://ko-fi.com/aaronfc" target="_blank" rel="noopener noreferrer" onClick={() => count('support')}>support Voidhead</a>. Meditation stays free.</p>
          <p><a href="mailto:blog@aaron.com.es?subject=Voidhead%20feedback" onClick={() => count('feedback')}>Tell Aarón how it went</a> — or ask for a personal follow-up. No account needed.</p>
          </div>
        </section>}
        {!playing && <section className="progress" aria-label="Your progress">
          <p><strong>{(stats.timeMeditated / 60).toLocaleString(undefined, { maximumFractionDigits: 1 })}</strong> {stats.timeMeditated === 60 ? 'minute' : 'minutes'} in <strong>{stats.sessions}</strong> {stats.sessions === 1 ? 'session' : 'sessions'}.</p>
          {user ? <>
            <p>Signed in as {user.email}</p>
            <button className="btn btn-link" onClick={this.logout}>Log out</button>
            <button className="btn btn-link" onClick={this.sync}>Sync progress</button>
          </> : <>
            <p className="small">Saved in this browser. Use an account to keep your progress across devices.</p>
            <div className="progress-actions">
              <button className="btn btn-secondary" onClick={() => this.setState({ accountOpen: true, accountMode: 'register' })}>Create account</button>
              <button className="btn btn-link" onClick={() => this.setState({ accountOpen: true, accountMode: 'login' })}>Log in</button>
            </div>
          </>}
          {this.state.syncMessage && <p className="small" role="status">{this.state.syncMessage}</p>}
        </section>}
        {this.state.accountOpen && !playing && <AccountForm key={this.state.accountMode} initialMode={this.state.accountMode}
          onClose={() => { this.setState({ accountOpen: false }); }}
          onSignedIn={() => { this.setState({ accountOpen: false }); }} />}
      </main>
      {!playing && <footer>
        {this.state.installAvailable && <button className="btn btn-link install" onClick={this.install}>Add to your home screen</button>}
        <p>Made by <a href="https://aaron.com.es">Aarón</a>. <a href="https://aaron.com.es/blog/tlc-2-voidhead/">The project story</a>.</p>
        <nav aria-label="About Voidhead">
          <a href="https://ko-fi.com/aaronfc" target="_blank" rel="noopener noreferrer" onClick={() => count('support')}>Support Voidhead</a>
          <a href="mailto:blog@aaron.com.es?subject=Voidhead%20feedback" onClick={() => count('feedback')}>Feedback</a>
          <a href="/privacy.html">Privacy</a>
          <a href="/tos.html">Terms</a>
        </nav>
      </footer>}
    </div>;
  }
}
export default App;
